/**
 * Member Membership Page - Olympic Fit Wellness
 * View and manage membership details
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  Calendar,
  Check,
  Star,
  ChevronRight,
  Download,
  Shield,
  AlertCircle,
  Loader2,
  RefreshCw,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

export default function MembershipPage() {
  const { profile } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const tiers = [
    {
      tier: 'essentials',
      name: 'Essentials',
      price: 49,
      features: ['All locations access', 'Group fitness classes', 'Locker room access', 'Fitness assessment', 'OFW mobile app']
    },
    {
      tier: 'wellness',
      name: 'Wellness',
      price: 99,
      highlight: true,
      features: ['Everything in Essentials', 'Personal trainer consultation', 'Recovery suite access', 'Nutrition guidance', 'Premium classes', 'Guest passes (2/month)']
    },
    {
      tier: 'elite',
      name: 'Elite',
      price: 199,
      features: ['Everything in Wellness', 'Unlimited personal training', 'Full spa & recovery access', 'Priority class booking', 'Exclusive events', 'Unlimited guest passes']
    }
  ];

  useEffect(() => {
    loadMembershipData();
  }, []);

  const loadMembershipData = async () => {
    setIsLoading(true);
    try {
      // Load subscription details
      const subData = await api.getMySubscription();
      setSubscription(subData);

      // Load payment history
      const historyData = await api.getPaymentHistory();
      setPaymentHistory(historyData?.transactions || []);
    } catch (error) {
      console.error('Error loading membership data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async (tierName) => {
    setIsProcessing(true);
    try {
      const result = await api.upgradeSubscription(tierName.toLowerCase(), window.location.origin);
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      alert('Failed to process upgrade. Please try again.');
    } finally {
      setIsProcessing(false);
      setShowUpgradeModal(false);
    }
  };

  const handleCancel = async () => {
    setIsProcessing(true);
    try {
      const result = await api.cancelSubscription(cancelReason);
      if (result?.success) {
        alert(result.message);
        loadMembershipData();
      }
    } catch (error) {
      console.error('Cancel error:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setIsProcessing(false);
      setShowCancelModal(false);
    }
  };

  const handleReactivate = async () => {
    setIsProcessing(true);
    try {
      const result = await api.reactivateSubscription();
      if (result?.success) {
        alert(result.message);
        loadMembershipData();
      }
    } catch (error) {
      console.error('Reactivate error:', error);
      alert('Failed to reactivate subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const currentTier = tiers.find(t => t.tier === (subscription?.tier || 'essentials'));
  const membershipStatus = subscription?.status || 'inactive';
  const isCancelled = membershipStatus === 'cancelled';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="membership-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-neutral-900">My Membership</h1>
        <p className="text-neutral-500">Manage your membership and billing</p>
      </div>

      {/* Current Plan Card */}
      <div className={`rounded-xl p-8 text-white ${isCancelled ? 'bg-gradient-to-br from-neutral-700 to-neutral-600' : 'bg-gradient-to-br from-neutral-900 to-neutral-800'}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400 font-medium">{currentTier?.name?.toUpperCase()} MEMBER</span>
              {isCancelled && (
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded ml-2">
                  CANCELLED
                </span>
              )}
            </div>
            <h2 className="text-3xl font-bold mb-1">${subscription?.amount || currentTier?.price}/month</h2>
            <p className="text-neutral-400">
              {subscription?.started_at 
                ? `Member since ${new Date(subscription.started_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
                : 'No active subscription'
              }
            </p>
            {isCancelled && (
              <p className="text-yellow-400 text-sm mt-2">
                Your subscription is cancelled but you have access until the end of your billing period.
              </p>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {isCancelled ? (
              <button
                onClick={handleReactivate}
                disabled={isProcessing}
                className="px-6 py-3 bg-green-500 text-white font-semibold text-sm hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                <RefreshCw className="w-4 h-4" />
                REACTIVATE
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="px-6 py-3 bg-white text-black font-semibold text-sm hover:bg-neutral-100 transition-colors"
                >
                  UPGRADE PLAN
                </button>
                <button className="px-6 py-3 border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-colors">
                  MANAGE
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Plan Benefits */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-neutral-100">
            <h2 className="text-xl font-heading font-bold text-neutral-900">Your Benefits</h2>
          </div>
          <div className="p-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {(subscription?.features || currentTier?.features || []).map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-neutral-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Info */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-neutral-900">Status</h3>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                membershipStatus === 'active' ? 'bg-green-100 text-green-700' :
                membershipStatus === 'cancelled' ? 'bg-red-100 text-red-700' :
                'bg-neutral-100 text-neutral-700'
              }`}>
                {membershipStatus.charAt(0).toUpperCase() + membershipStatus.slice(1)}
              </span>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-500">Next billing</span>
                <span className="font-medium">
                  {subscription?.next_billing_date 
                    ? new Date(subscription.next_billing_date).toLocaleDateString()
                    : 'N/A'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Auto-renew</span>
                <span className={`font-medium ${subscription?.auto_renew ? 'text-green-600' : 'text-red-600'}`}>
                  {subscription?.auto_renew ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Method
            </h3>
            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-6 bg-gradient-to-br from-blue-600 to-blue-800 rounded" />
                <span className="font-mono text-sm">**** **** **** 4242</span>
              </div>
              <button className="text-[#3B82F6] text-sm font-medium hover:underline">
                Update
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-heading font-bold text-neutral-900">Billing History</h2>
          <button className="text-[#3B82F6] text-sm font-medium hover:underline flex items-center gap-1">
            <Download className="w-4 h-4" />
            Download All
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-500 uppercase">Invoice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {paymentHistory.length > 0 ? (
                paymentHistory.map((item, idx) => (
                  <tr key={idx} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 text-sm text-neutral-900">
                      {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600">{item.description}</td>
                    <td className="px-6 py-4 text-sm font-medium text-neutral-900">${item.amount?.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        item.status === 'paid' ? 'bg-green-100 text-green-700' :
                        item.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-neutral-100 text-neutral-700'
                      }`}>
                        {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="text-[#3B82F6] text-sm font-medium hover:underline">
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-neutral-400">
                    No payment history yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-heading font-bold text-neutral-900 mb-4">Membership Actions</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <button className="p-4 border border-neutral-200 rounded-lg text-left hover:border-neutral-400 transition-colors">
            <Shield className="w-6 h-6 text-neutral-600 mb-2" />
            <h4 className="font-medium text-neutral-900">Freeze Membership</h4>
            <p className="text-sm text-neutral-500">Pause your membership temporarily</p>
          </button>
          <button className="p-4 border border-neutral-200 rounded-lg text-left hover:border-neutral-400 transition-colors">
            <Calendar className="w-6 h-6 text-neutral-600 mb-2" />
            <h4 className="font-medium text-neutral-900">Change Billing Date</h4>
            <p className="text-sm text-neutral-500">Update your billing cycle</p>
          </button>
          {!isCancelled && (
            <button 
              onClick={() => setShowCancelModal(true)}
              className="p-4 border border-red-200 rounded-lg text-left hover:border-red-400 transition-colors"
            >
              <AlertCircle className="w-6 h-6 text-red-500 mb-2" />
              <h4 className="font-medium text-red-600">Cancel Membership</h4>
              <p className="text-sm text-neutral-500">We'll be sad to see you go</p>
            </button>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
              <h2 className="text-2xl font-heading font-bold">Upgrade Your Membership</h2>
              <button onClick={() => setShowUpgradeModal(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-3 gap-6">
                {tiers.map((tier) => (
                  <div 
                    key={tier.tier}
                    className={`rounded-xl border-2 p-6 ${
                      tier.tier === subscription?.tier 
                        ? 'border-green-500 bg-green-50' 
                        : tier.highlight 
                          ? 'border-[#3B82F6]' 
                          : 'border-neutral-200'
                    }`}
                  >
                    {tier.highlight && tier.tier !== subscription?.tier && (
                      <span className="text-xs font-semibold text-[#3B82F6] uppercase">Most Popular</span>
                    )}
                    {tier.tier === subscription?.tier && (
                      <span className="text-xs font-semibold text-green-600 uppercase">Current Plan</span>
                    )}
                    <h3 className="text-xl font-bold mt-2">{tier.name}</h3>
                    <div className="text-3xl font-bold mt-2">${tier.price}<span className="text-sm font-normal text-neutral-500">/mo</span></div>
                    <ul className="mt-4 space-y-2">
                      {tier.features.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handleUpgrade(tier.tier)}
                      disabled={tier.tier === subscription?.tier || isProcessing}
                      className={`w-full mt-6 py-3 rounded-lg font-semibold text-sm transition-colors ${
                        tier.tier === subscription?.tier
                          ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                          : 'bg-[#3B82F6] text-white hover:bg-[#2563eb]'
                      }`}
                    >
                      {tier.tier === subscription?.tier ? 'Current Plan' : 'Select Plan'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-neutral-100">
              <h2 className="text-xl font-heading font-bold text-red-600">Cancel Membership</h2>
            </div>
            <div className="p-6">
              <p className="text-neutral-600 mb-4">
                We're sorry to see you go. Your membership will remain active until the end of your current billing period.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Please tell us why you're leaving (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full p-3 border border-neutral-200 rounded-lg resize-none"
                  rows={3}
                  placeholder="Your feedback helps us improve..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 py-3 border border-neutral-200 rounded-lg font-semibold text-sm hover:bg-neutral-50"
                >
                  Keep Membership
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isProcessing}
                  className="flex-1 py-3 bg-red-500 text-white rounded-lg font-semibold text-sm hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                  Cancel Membership
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
